/**
 * UserCreateAccessFields — F12.M user create/edit unify (create-mode variant zakładki
 * „Firmy" / company_access).
 *
 * `UserCompanyAccessTab` czyta dostęp per firma przez `user_company_access`, co
 * wymaga istniejącego `userId` — w trybie create rekordu jeszcze nie ma. Tu user
 * wybiera firmy (multi) + role w kolejności (tylko dla „człowiek", ADR-0103: jedna lub
 * wiele; pierwsza = główna) PRZED utworzeniem konta;
 * wybór konsumuje `handleCreateSubmit` w `UserDetail.tsx` (POST /users/invite lub
 * POST /users z X-Company-Id).
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Dropdown } from '@/components/Dropdown';
import { InfoHint } from '@/components/InfoHint';
import { MultiSelect } from '@/components/MultiSelect';
import { type RoleTemplate, useCompanies, useRoleTemplates } from '@/lib/use-permissions';

import { CompanyRolesEditor } from './CompanyRolesEditor';
import type { UserFormData } from './UserDataTab';

export interface UserCreateAccessFieldsProps {
  userType: UserFormData['userType'];
  companyIds: string[];
  onCompanyIdsChange: (value: string[]) => void;
  /** Role w kolejności (pierwsza = główna) — kopie z pierwszej wybranej firmy. */
  roleTemplateIds: string[];
  onRoleTemplateIdsChange: (value: string[]) => void;
  /** Domyślna firma konta — wybierana spośród `companyIds`; leci w zapisie (human). */
  defaultCompanyId: string;
  onDefaultCompanyIdChange: (value: string) => void;
}

export function UserCreateAccessFields({
  userType,
  companyIds,
  onCompanyIdsChange,
  roleTemplateIds,
  onRoleTemplateIdsChange,
  defaultCompanyId,
  onDefaultCompanyIdChange,
}: UserCreateAccessFieldsProps) {
  const { t } = useTranslation();
  const companiesQ = useCompanies();
  const rolesQ = useRoleTemplates();
  const companies = companiesQ.data?.companies ?? [];
  const roleTemplates = rolesQ.data?.roleTemplates;
  const isHuman = userType === 'human';
  // Domyślną firmę wybieramy tylko spośród przypisanych i tylko gdy jest z czego
  // wybierać (>=2). Przy jednej firmie domyślna jest oczywista (sync w UserDetail).
  const selectedCompanies = companies.filter((c) => companyIds.includes(c.id));
  const roleOptions = useMemo(
    () => rolesAvailableInCompanies(roleTemplates ?? [], companyIds),
    [roleTemplates, companyIds],
  );
  // Zmiana firm może unieważnić wybrane role (brak ich kopii w nowo dodanej firmie) —
  // wtedy je wycinamy zamiast wysłać role, których backend nie przyjmie.
  useEffect(() => {
    if (roleTemplateIds.length === 0 || rolesQ.isLoading) return;
    const stillValid = roleTemplateIds.filter((id) => roleOptions.some((r) => r.id === id));
    if (stillValid.length !== roleTemplateIds.length) onRoleTemplateIdsChange(stillValid);
  }, [roleOptions, roleTemplateIds, rolesQ.isLoading, onRoleTemplateIdsChange]);
  const assignedRoles = roleTemplateIds
    .map((id) => roleOptions.find((r) => r.id === id))
    .filter((r): r is RoleTemplate => r !== undefined)
    .map((r) => ({ id: r.id, name: r.name }));

  return (
    <div className="space-y-4 p-4" data-testid="user-create-access-fields">
      <div>
        <label className="mb-1 block text-xs text-text-tertiary">
          {t('tenant.permissions.addCompany.companyLabel')} *
        </label>
        {isHuman ? (
          <MultiSelect
            value={companyIds}
            onChange={onCompanyIdsChange}
            options={companies.map((c) => ({ value: c.id, label: c.legalName }))}
            data-testid="user-create-company"
          />
        ) : (
          // Konto techniczne działa w kontekście JEDNEJ firmy (POST /users bierze ją
          // z nagłówka X-Company-Id) — pojedynczy wybór zamiast listy wielokrotnej.
          <Dropdown
            value={companyIds[0] ?? ''}
            onChange={(v) => onCompanyIdsChange(v ? [v] : [])}
            options={companies.map((c) => ({ value: c.id, label: c.legalName }))}
            placeholder={t('tenant.permissions.addCompany.companyLabel')}
            data-testid="user-create-company"
          />
        )}
      </div>
      {isHuman && (
        <div>
          <label className="mb-1 block text-xs text-text-tertiary">
            {t('tenant.users.detail.create.rolesLabel')}
          </label>
          {companyIds.length > 0 && (
            <div data-testid="user-create-role">
              <CompanyRolesEditor
                companyId="new"
                assigned={assignedRoles}
                available={roleOptions.map((r) => ({
                  id: r.id,
                  name: r.name,
                  isSystem: r.isSystem,
                }))}
                onChange={onRoleTemplateIdsChange}
                busy={rolesQ.isLoading}
              />
            </div>
          )}
          {assignedRoles.length > 1 && <InfoHint>{t('tenant.users.roles.hint')}</InfoHint>}
          <p className="mt-1 text-xs text-text-tertiary">
            {companyIds.length === 0
              ? t('tenant.users.detail.create.roleHintNoCompany')
              : companyIds.length > 1
                ? t('tenant.users.detail.create.roleHintMulti')
                : t('tenant.users.detail.create.roleHint')}
          </p>
        </div>
      )}
      {isHuman && selectedCompanies.length >= 2 && (
        <div>
          <label className="mb-1 block text-xs text-text-tertiary">
            {t('tenant.users.detail.create.defaultCompanyLabel')}
          </label>
          <Dropdown
            value={defaultCompanyId}
            onChange={onDefaultCompanyIdChange}
            options={selectedCompanies.map((c) => ({ value: c.id, label: c.legalName }))}
            placeholder={t('tenant.users.detail.create.defaultCompanyPlaceholder')}
            data-testid="user-create-default-company"
          />
          <p className="mt-1 text-xs text-text-tertiary">
            {t('tenant.users.detail.create.defaultCompanyHint')}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Role do wyboru przy zakładaniu konta: tylko linie rodowe ról, które mają kopię W KAŻDEJ
 * wybranej firmie (role są per firma — ta sama „Księgowa" w firmie A i B to dwa rekordy
 * o wspólnym `roleKey`). Każda linia raz; wartość = kopia z pierwszej wybranej firmy
 * (backend i tak dobiera właściwą kopię dla każdej firmy). Bez wybranej firmy — pusto.
 */
export function rolesAvailableInCompanies(
  roles: readonly RoleTemplate[],
  companyIds: readonly string[],
): RoleTemplate[] {
  if (companyIds.length === 0) return [];
  const byKey = new Map<string, RoleTemplate[]>();
  for (const r of roles) {
    if (!r.companyId || !companyIds.includes(r.companyId)) continue;
    const key = r.roleKey ?? r.id;
    const list = byKey.get(key) ?? [];
    list.push(r);
    byKey.set(key, list);
  }
  const result: RoleTemplate[] = [];
  for (const list of byKey.values()) {
    const coversAll = companyIds.every((c) => list.some((r) => r.companyId === c));
    if (!coversAll) continue;
    result.push(list.find((r) => r.companyId === companyIds[0]) ?? list[0]!);
  }
  return result.sort((a, b) => a.name.localeCompare(b.name, 'pl'));
}
